import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Donor } from '../../../interfaces/donor';
import { Recipient } from '../../../interfaces/recipient';
import { Pair } from '../../../interfaces/pair';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PagetoolbarComponent } from '../../pagetoolbar/pagetoolbar.component';
import { MatFormField } from '@angular/material/form-field';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ParticipantService } from '../../../services/participant.service';
import { PairService } from '../../../services/pair.service';
import { MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { CenterService } from '../../../services/center.service';
import { Center } from '../../../interfaces/center';
import moment from 'moment';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';
import { AuthService } from '../../../auth/auth.service';
import { MatchingPeriodService } from '../../../services/matchingPeriod.service';
import { MatchingPeriod } from '../../../interfaces/matchingPeriod';
import { firstValueFrom, Observable, of, throwError, forkJoin, switchMap } from 'rxjs';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';

export const MY_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY'
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-participant-add',
  imports: [
    ReactiveFormsModule,
    MatSnackBarModule,
    MatIconModule,
    MatSelectModule,
    CommonModule,
    PagetoolbarComponent,
    MatFormField,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogContent,
    MatNativeDateModule,
    MatDatepickerModule,
    MatOptionModule,
    MatCheckboxModule,
    MatMomentDateModule,
    MatTabsModule
  ],
  templateUrl: './participant-add.component.html',
  styleUrl: './participant-add.component.scss',
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class ParticipantAddComponent {
  donorForm!: FormGroup;
  recipientForm!: FormGroup;
  pairForm!: FormGroup;
  selectedTabIndex: number = 0; // 0 = pair, 1 = donor, 2 = recipient
  centers!: Center[];
  assignableCenters!: Center[];
  currentMatchingPeriod?: MatchingPeriod;
  relations: string[] = ['Parent', 'Child', 'Partner', 'Sibling', 'Grandparent', 'Grandchild', 'Nephew', 'Niece', 'Uncle', 'Aunt', 'FamilyNotRelated', 'DonorByMedia', 'NotRelated'];
  currentDonorIndex: number = 0; // Track which donor is currently displayed

  //Send back new participant to list
  responseSubjectNewDonor: Subject<Donor> = new Subject<Donor>();
  responseSubjectNewRecipient: Subject<Recipient> = new Subject<Recipient>();
  responseSubjectNewPair: Subject<Pair> = new Subject<Pair>();
  privileges: boolean = false;
  donorPrivileges: boolean = false;

  get donorFormsArray(): FormArray {
    return this.pairForm.get('donors') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private participantService: ParticipantService,
    private pairService: PairService,
    private centerService: CenterService,
    private matchingPeriodService: MatchingPeriodService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<ParticipantAddComponent>,
    private readonly authService: AuthService
  ) {}
  ngOnInit(): void {

    // Initialize Pair form (donor(s) + recipient together)
    this.pairForm = this.fb.group({
      donors: this.fb.array([]), // FormArray for multiple donors
      recipientEnisNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]*$/)
        ]
      ],
      recipientDob: ['', [Validators.required]],
      recipientCenter: ['', [Validators.required]],
      compatible: [false],
      comments: [''],
      addPairToMatchingPeriod: [true]
    });

    // Add the first donor form
    this.addDonorFormGroup();

    // Initialize Donor form
    this.donorForm = this.fb.group({
      dob: ['', [Validators.required]],
      center: ['', [Validators.required]],
      enisNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]*$/) // Ensure only digits are allowed
        ]
      ],
      altruist: [false],
      active: [true]
    });

    // Initialize Recipient form
    this.recipientForm = this.fb.group({
      dob: ['', [Validators.required]],
      center: ['', [Validators.required]],
      enisNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]*$/) // Ensure only digits are allowed
        ]
      ]
    });

    this.getCurrentMatchingPeriod();
    this.setRole();
  }

  async setRole() {
    await this.getAllCenters();
    const user = await firstValueFrom(this.authService.getUser());
    console.log('User from auth service:', user);
    if (user.activeRole.name === 'Center') {
      // Find the center object by name
      const userCenterObj = this.centers.find((c) => c.name === user.centerName);
      console.log('User center object:', userCenterObj);
      if (userCenterObj) {
        this.donorForm.get('center')?.setValue(userCenterObj);
        this.recipientForm.get('center')?.setValue(userCenterObj);
        this.pairForm.get('recipientCenter')?.setValue(userCenterObj);
        // Set center for all donor forms in the array
        this.donorFormsArray.controls.forEach(control => {
          control.get('center')?.setValue(userCenterObj);
          control.get('center')?.disable();
        });
      }
      // Disable the center selection in all forms
      this.donorForm.get('center')?.disable();
      this.recipientForm.get('center')?.disable();
      this.pairForm.get('recipientCenter')?.disable();
    }
  }

  private createDonorFormGroup(): FormGroup {
    return this.fb.group({
      enisNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(6),
          Validators.pattern(/^[0-9]*$/)
        ]
      ],
      dob: ['', [Validators.required]],
      center: ['', [Validators.required]],
      relationToRecipient: ['', [Validators.required]]
    });
  }

  private addDonorFormGroup(): void {
    const donorFormGroup = this.createDonorFormGroup();
    this.donorFormsArray.push(donorFormGroup);
  }

  addAnotherDonor(): void {
    if (this.donorFormsArray.length < 3) {
      this.addDonorFormGroup();
      this.currentDonorIndex = this.donorFormsArray.length - 1; // Switch to the new donor
    }
  }

  removeDonor(index: number): void {
    if (this.donorFormsArray.length > 1) {
      this.donorFormsArray.removeAt(index);
      // Adjust currentDonorIndex if needed
      if (this.currentDonorIndex >= this.donorFormsArray.length) {
        this.currentDonorIndex = this.donorFormsArray.length - 1;
      }
    }
  }

  switchDonor(index: number): void {
    this.currentDonorIndex = index;
  }

  getCurrentMatchingPeriod() {
    this.matchingPeriodService.getLatestMatchingPeriod().subscribe((currentMatchingPeriodResult) => {
      if (currentMatchingPeriodResult != null) {
        this.currentMatchingPeriod = currentMatchingPeriodResult;
      }
    });
  }



  resetForm(): void {
    this.donorForm.reset();
    this.recipientForm.reset();
    this.pairForm.reset({ compatible: false, addPairToMatchingPeriod: true });
  }
  submitDonor(): void {
    if (this.donorForm.invalid) {
      this.showNotification('Please fill out all required fields correctly.');
      return;
    }

    // Enable center control to include its value
    const centerControl = this.donorForm.get('center');
    centerControl?.enable({ emitEvent: false });
    const donorData: Donor = {
      ...this.donorForm.value,
      dob: this.formatDate(this.donorForm.value.dob)
    };
    // Optionally disable again
    if (this.donorForm.get('center')?.disabled === false) {
      this.donorForm.get('center')?.disable({ emitEvent: false });
    }

    this.participantService.createDonor(donorData).subscribe({
      next: (response) => {
        this.showNotification('Donor created successfully!');
        this.responseSubjectNewDonor.next(response);
        this.donorForm.reset({ altruist: false });
        this.dialogRef.close();
      },
      error: () => {
        this.showNotification('Failed to create donor. Please try again.');
      }
    });
  }

  private formatDate(date: string): string | null {
    if (!date) return null;
    let momentDate = moment(date);
    if (!moment.isMoment(momentDate)) {
      throw new Error('Invalid date');
    }
    const year = momentDate.year();
    const month = momentDate.format('MM'); // Use Moment.js method to format month
    const day = momentDate.format('DD'); // Use Moment.js method to format day

    return `${year}-${month}-${day}`;
  }

  submitRecipient(): void {
    if (this.recipientForm.invalid) {
      this.showNotification('Please fill out all required fields correctly.');
      return;
    }

    // Enable center control to include its value
    const centerControl = this.recipientForm.get('center');
    centerControl?.enable({ emitEvent: false });
    const recipientData: Recipient = {
      ...this.recipientForm.value,
      dob: this.formatDate(this.recipientForm.value.dob)
    };
    // Optionally disable again
    if (this.recipientForm.get('center')?.disabled === false) {
      this.recipientForm.get('center')?.disable({ emitEvent: false });
    }

    this.participantService.createRecipient(recipientData).subscribe({
      next: (response) => {
        this.showNotification('Recipient created successfully!');
        this.responseSubjectNewRecipient.next(response);
        this.recipientForm.reset();
        this.dialogRef.close();
      },
      error: () => {
        this.showNotification('Failed to create recipient. Please try again.');
      }
    });
  }

  submitPair(): void {
    if (this.pairForm.invalid) {
      this.showNotification('Please fill out all required fields correctly.');
      return;
    }

    // Enable center controls to include their values
    const recipientCenterControl = this.pairForm.get('recipientCenter');
    recipientCenterControl?.enable({ emitEvent: false });

    const recipientDob = this.formatDate(this.pairForm.value.recipientDob);

    if (!recipientDob) {
      this.showNotification('Invalid recipient date format. Please check the date.');
      return;
    }

    const recipientData: Recipient = {
      enisNumber: this.pairForm.value.recipientEnisNumber,
      dob: recipientDob,
      center: this.pairForm.value.recipientCenter
    };

    // Optionally disable again
    if (recipientCenterControl?.disabled === false) {
      recipientCenterControl.disable({ emitEvent: false });
    }

    // Create recipient first
    this.participantService.createRecipient(recipientData).subscribe({
      next: (createdRecipient) => {
        this.responseSubjectNewRecipient.next(createdRecipient);

        // Create donors and pairs for each donor in the array
        const donorsFormValues = this.donorFormsArray.getRawValue();
        const donorCreationObservables: Observable<any>[] = [];

        donorsFormValues.forEach((donorFormValue: any, index: number) => {

          const donorDob = this.formatDate(donorFormValue.dob);

          if (!donorDob) {
            this.showNotification(`Invalid donor ${index + 1} date format. Please check the date.`);
            return;
          }

          const donorData: Donor = {
            enisNumber: donorFormValue.enisNumber,
            dob: donorDob,
            center: donorFormValue.center,
            altruist: false,
            active: true
          };

          // Create an observable that creates the donor and then the pair
          const donorPairObservable = this.participantService.createDonor(donorData).pipe(
            switchMap((createdDonor: Donor) => {
              this.responseSubjectNewDonor.next(createdDonor);

              const pairData: any = {
                donor: createdDonor,
                recipient: createdRecipient,
                relationToRecipient: donorFormValue.relationToRecipient,
                compatible: this.pairForm.value.compatible,
                comments: this.pairForm.value.comments,
                // Requested state only; the backend decides the final state of pair + both
                // participants and handles the matching period membership (CROS-398).
                active: this.pairForm.value.addPairToMatchingPeriod === true,
                addToMatchingPeriod: this.pairForm.value.addPairToMatchingPeriod === true
              };

              return this.pairService.createPair(pairData);
            })
          );

          donorCreationObservables.push(donorPairObservable);
        });

        // Execute all donor/pair creations in parallel
        forkJoin(donorCreationObservables).subscribe({
          next: (pairResponses) => {
            this.showNotification(`${pairResponses.length} pair(s) created successfully!`);
            pairResponses.forEach(pairResponse => {
              this.responseSubjectNewPair.next(pairResponse);
            });
            this.pairForm.reset({ compatible: false, addPairToMatchingPeriod: true });
            this.dialogRef.close();
          },
          error: (error) => {
            this.showNotification('Failed to create one or more pairs. Please try again.');
            console.error('Error creating pairs:', error);
          }
        });
      },
      error: (error) => {
        this.showNotification('Failed to create recipient. Please try again.');
        console.error('Error creating recipient:', error);
      }
    });
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

// Create an async version of getAllCenters
private getAllCenters(): Promise<void> {
  return new Promise((resolve, reject) => {
    this.centerService.getAllCenters().subscribe({
      next: (data) => {
        this.centers = data;
        // Filter out UNKNOWN center for assignment dropdown
        this.assignableCenters = this.centers.filter((center) => center.name !== 'UNKNOWN');
        console.log('Centers loaded:', this.centers);
        resolve();
      },
      error: (error) => {
        console.error('Error loading centers:', error);
        reject(error);
      }
    });
  });
}

}
